import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Icon, BodyText, HeadingText } from '@paypalcorp/pp-react';

import { parseData } from '../../../lib/helpers';
import WrappedLink from './WrappedLink';
import { useScreenSizeInfo } from '../ScreenSizeInfoProvider';
import { AMPLITUDE } from '../../../lib/constants';
import { useLocation } from 'react-router-dom';

const { NAV_CLICK } = AMPLITUDE;

const getProd = (toolContent, appKey) =>
  appKey ? toolContent[appKey]?.fpti : null;

const getCategory = (appKey, categoryKey) =>
  !appKey && categoryKey ? 'parent' : null;

const renderTitleHeader = (
  showRebrandNameAlt1,
  isNewLayoutEligible,
  brandNames,
  mainContent,
  isLoggedIn
) => {
  if (showRebrandNameAlt1 && isNewLayoutEligible && brandNames) {
    return brandNames.title2;
  }

  if (!isLoggedIn && brandNames) {
    return brandNames.title2;
  }

  return mainContent.label;
};

const TitleAndBreadCrumbs = props => {
  const {
    location: { pathname = '/appcenter', state: { prevPath } = {} },
    flatPSData: {
      categories = {},
      tools = {},
      isNewLayoutEligible,
      collections = []
    },
    experimentation: { showRebrandNameAlt1 } = {},
    structuredPSData: { content: { backButton, rebrandingNames } = {} } = {},
    fptiClick,
    app: {
      context: {
        user: { isLoggedIn } = {},
        locale: { directionality } = {}
      } = {}
    } = {}
  } = props;

  let brandNames = {};
  if (rebrandingNames) {
    brandNames = parseData(rebrandingNames)[0];
  }

  const mainContent = {
    label: isNewLayoutEligible
      ? get(props, 'flatPSData.main[0].header', '')
      : get(props, 'flatPSData.main[0].title', ''),
    fpti: 'main'
  };

  const categoryContent = useMemo(() => {
    return Object.values(categories).reduce((acc, category) => {
      acc[category.location] = {
        label: category.categoryTitle,
        fpti: category.categoryFPTI,
        categoryKey: category.categoryKey
      };
      return acc;
    }, {});
  }, [categories]);
  const toolContent = useMemo(() => {
    return Object.values(tools).reduce((acc, tool) => {
      acc[tool.location] = {
        label: tool.appTitle,
        fpti: tool.appFPTI,
        appKey: tool.appKey
      };
      return acc;
    }, {});
  }, [tools]);

  const collectionContent = useMemo(() => {
    return Object.values(collections).reduce((acc, collection) => {
      acc[collection.location] = {
        label: collection.title,
        fpti: collection.fptiLinkName,
        appKey: collection.collectionKey
      };
      return acc;
    }, {});
  }, [collections]);

  const [, mainSlug, categorySlug, detailsSlug] = pathname.split('/');
  const paths = [mainSlug, categorySlug, detailsSlug]
    .filter(slug => {
      return slug && !(slug === detailsSlug && categorySlug === 'collection');
    })
    .map(slug => {
      const isCollections = slug === categorySlug && slug === 'collection';
      if (isCollections) {
        return {
          path: `/${mainSlug}/${categorySlug}/${detailsSlug}`,
          ...collectionContent[detailsSlug]
        };
      } else if (slug === detailsSlug) {
        return {
          path: `/${mainSlug}/${categorySlug}/${detailsSlug}`,
          ...toolContent[detailsSlug]
        };
      } else if (slug === categorySlug) {
        return {
          path: `/${mainSlug}/${categorySlug}`,
          ...categoryContent[categorySlug]
        };
      }
      return { path: `/${slug}`, ...mainContent };
    });

  const getOnClick = ({ fpti, categoryKey, appKey }) => {
    return () => {
      // add prod <product> for details pages and category 'parent' for category pages
      fptiClick({
        fptiLink: fpti,
        prod: getProd(toolContent, appKey),
        category: getCategory(appKey, categoryKey),
        amplitude: true,
        event_name: NAV_CLICK
      });
    };
  };

  const titleHeader = renderTitleHeader(
    showRebrandNameAlt1,
    isNewLayoutEligible,
    brandNames,
    mainContent,
    isLoggedIn
  );
  return (
    <section id="appcenter-title-and-bread-crumbs">
      {paths.length === 1 ? (
        <HeadingText as="h1" size="md">
          {titleHeader}
        </HeadingText>
      ) : (
        [
          isNewLayoutEligible ? (
            <BackButton
              content={paths}
              label={backButton}
              getOnClick={getOnClick}
              prevPath={prevPath}
            />
          ) : (
            <BreadCrumbs
              getOnClick={getOnClick}
              content={paths}
              directionality={directionality}
            />
          )
        ]
      )}
    </section>
  );
};

const BackButton = ({ label, content, getOnClick, prevPath }) => {
  const location = useLocation();
  const filtered = content.filter(c => c.path === prevPath)[0];
  const prevContent =
    prevPath?.includes('/collection/') ||
    (prevPath?.includes('/setup/checkout') &&
      location.pathname.includes('collection/no-website-apps'))
      ? { path: prevPath }
      : filtered || content[0];

  return (
    <div className="back-button">
      <Icon size="sm" name={'chevron-left'} />
      <BodyText
        size="lg"
        medium
        className="last add-focus-visible"
        as={WrappedLink}
        to={prevContent.path}
        onClick={getOnClick(prevContent)}
      >
        {label}
      </BodyText>
    </div>
  );
};

const BreadCrumbs = ({ content = [], getOnClick, directionality }) => {
  const { isMobile } = useScreenSizeInfo();
  if (!content.length) {
    return null;
  }
  const chevronIcon =
    directionality === 'rtl' ? 'chevron-left' : 'chevron-right';

  const renderedBreadcrumbs = content.map((item, i) => {
    const { fpti, categoryKey, appKey } = item;
    return (
      <>
        {i ? <Icon size="sm" name={chevronIcon} /> : null}
        <BodyText
          size="lg"
          as={WrappedLink}
          className="add-focus-visible"
          to={item.path}
          onClick={getOnClick({ fpti, categoryKey, appKey })}
        >
          {item.label}
        </BodyText>
      </>
    );
  });
  renderedBreadcrumbs[renderedBreadcrumbs.length - 1] = (
    <>
      <Icon size="sm" name={chevronIcon} />
      <BodyText
        size="lg"
        medium
        className="last add-focus-visible"
        as={WrappedLink}
        to={content[content.length - 1].path}
        onClick={getOnClick(content[content.length - 1])}
      >
        {content[content.length - 1].label}
      </BodyText>
    </>
  );

  if (isMobile) {
    renderedBreadcrumbs[0] = (
      <Icon
        size="xl"
        name="store"
        as={WrappedLink}
        className="add-focus-visible"
        aria-label={content[0].label}
        to={'/appcenter'}
        onClick={getOnClick(content[0])}
      />
    );
    renderedBreadcrumbs.splice(1, renderedBreadcrumbs.length - 2);
  }

  return renderedBreadcrumbs;
};

BreadCrumbs.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string
    })
  ),
  getOnClick: PropTypes.func,
  directionality: PropTypes.string.isRequired
};

BackButton.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string
    })
  ),
  getOnClick: PropTypes.func,
  prevPath: PropTypes.string,
  label: PropTypes.string
};

TitleAndBreadCrumbs.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.object
  }).isRequired,
  flatPSData: PropTypes.shape({
    categories: PropTypes.object,
    tools: PropTypes.object,
    collections: PropTypes.array,
    isNewLayoutEligible: PropTypes.bool
  }).isRequired,
  structuredPSData: PropTypes.shape({
    content: PropTypes.object
  }),
  experimentation: PropTypes.object,
  fptiClick: PropTypes.func.isRequired,
  categories: PropTypes.array,
  app: PropTypes.object.isRequired,
  tools: PropTypes.object,
  ps: PropTypes.object,
  dispatch: PropTypes.func,
  clearSearch: PropTypes.func
};

export default TitleAndBreadCrumbs;
